/**
 * MD-28 Agent + Cron Orchestration Dashboard.
 *
 * Spec: specs/domains/admin/F-A-AGENT-DASHBOARD.md (locked 2026-05-12, Plan A).
 * Decision: DECISIONS s2026-05-12-AGENT-DASHBOARD-MD-28-PLAN-A.
 *
 * Section:
 * 1. Agent roster (7 card grid, static tu pages/src/data/agents.json)
 * 2. Cron job table (11 row, dynamic tu /api/v1/admin/cron-jobs + next-run client-side)
 * 3. Activity log (R2 audit tail 50 line, 48h window)
 * 4. Workflow ASCII (cron -> owner agent)
 */

import * as React from 'react';
import { useMemo, useState } from 'react';
import { DashboardGate } from './DashboardGate';
import { DashboardLayout } from './DashboardSidebar';
import { useAdminAgentsApi } from './use-admin-agents';
import { nextCronRun, relativeIn } from './cron-next';
import agentsData from '../../data/agents.json';
import cronJobsData from '../../data/cron-jobs.json';

interface AgentMeta {
  id: string;
  display_name: string;
  model: string;
  tools: string[];
  veto_power: boolean;
  veto_scope: string | null;
  trigger_hint: string;
  definition_path: string;
  priority?: number;
}

interface CronJsonRow {
  expr: string;
  name: string;
  module: string;
  owner_agent: string;
  human_ict?: string;
  description?: string;
  active?: boolean;
  wrangler_comment?: string;
}

interface CronApiRow {
  expr: string;
  name: string;
  human_ict: string;
  module: string;
  owner_agent: string;
  description: string;
  period_min: number;
  active?: boolean;
  log_source: { type: string; bucket?: string; prefix?: string; key?: string; table?: string };
  last_run?: {
    ts: string | null;
    status: 'ok' | 'warn' | 'fail' | 'idle';
    summary: string;
  };
}

interface CronApiResponse {
  crons: CronApiRow[];
  generated_at: string;
}

interface ActivityResponse {
  lines: Array<{ ts: string; cron: string; line: string }>;
  window: string;
}

type StatusKind = 'ok' | 'warn' | 'fail' | 'idle';
const STATUS_LABEL: Record<StatusKind, string> = {
  ok: 'OK',
  warn: 'Canh bao',
  fail: 'Loi',
  idle: 'Cho',
};

function StatusPill({ status }: { status: StatusKind }) {
  return (
    <span className={`agent-status-pill agent-status-pill-${status}`}>{STATUS_LABEL[status]}</span>
  );
}

function VetoBadge({ veto, scope }: { veto: boolean; scope: string | null }) {
  if (!veto) return <span className="agent-veto-badge agent-veto-badge-no">Khong veto</span>;
  return (
    <span className="agent-veto-badge agent-veto-badge-yes" title={scope ?? ''}>
      HARD VETO
    </span>
  );
}

function AgentCard({ agent, cronCount }: { agent: AgentMeta; cronCount: number }) {
  return (
    <div className="agent-card">
      <div className="agent-card-head">
        <h3 className="agent-card-title">{agent.display_name}</h3>
        <VetoBadge veto={agent.veto_power} scope={agent.veto_scope} />
      </div>
      <p className="agent-card-id">{agent.id}</p>
      <p className="agent-card-trigger">{agent.trigger_hint}</p>
      <div className="agent-card-foot">
        <span className="agent-card-cron-count">
          {cronCount > 0 ? `${cronCount} cron so huu` : 'Khong so huu cron'}
        </span>
        <span className="agent-card-model">model: {agent.model}</span>
      </div>
    </div>
  );
}

function AgentRosterSection({
  agents,
  cronCountByAgent,
  filter,
}: {
  agents: AgentMeta[];
  cronCountByAgent: Record<string, number>;
  filter: string;
}) {
  const f = filter.trim().toLowerCase();
  // Sort priority asc (1=top), tie-break theo cron owned desc, roi alpha.
  // Per founder feedback 2026-05-12: agent quan trong dua len top.
  const sorted = [...agents].sort((a, b) => {
    const pa = a.priority ?? 3;
    const pb = b.priority ?? 3;
    if (pa !== pb) return pa - pb;
    const ca = cronCountByAgent[a.id] ?? 0;
    const cb = cronCountByAgent[b.id] ?? 0;
    if (ca !== cb) return cb - ca;
    return a.display_name.localeCompare(b.display_name);
  });
  const visible = f
    ? sorted.filter(
        (a) =>
          a.id.toLowerCase().includes(f) ||
          a.display_name.toLowerCase().includes(f) ||
          a.trigger_hint.toLowerCase().includes(f),
      )
    : sorted;

  return (
    <section className="agent-section">
      <h2 className="agent-section-title">1. Agent dinh nghia ({visible.length}/{agents.length})</h2>
      <p className="agent-section-sub">
        Cac agent duoc dispatch trong Claude Code IDE (nam ngoai CF Workers). Trang nay chi hien
        thi <em>vai tro va dinh nghia</em>, KHONG hien trang thai chay realtime cho moi luot dispatch.
      </p>
      <div className="agent-card-grid">
        {visible.map((a) => (
          <AgentCard key={a.id} agent={a} cronCount={cronCountByAgent[a.id] ?? 0} />
        ))}
        {visible.length === 0 && (
          <p className="agent-empty">Khong co agent nao khop tu khoa "{filter}".</p>
        )}
      </div>
    </section>
  );
}

function formatLastRun(row: CronApiRow): { tsLabel: string; status: StatusKind; summary: string } {
  const status = (row.last_run?.status ?? 'idle') as StatusKind;
  const summary = row.last_run?.summary ?? '';
  if (!row.last_run?.ts) return { tsLabel: '-', status, summary };
  const t = new Date(row.last_run.ts);
  const HH = String(t.getUTCHours() + 7).padStart(2, '0').slice(-2);
  const realHH = String(((t.getUTCHours() + 7) % 24)).padStart(2, '0');
  const mm = String(t.getUTCMinutes()).padStart(2, '0');
  const DD = String(t.getUTCDate()).padStart(2, '0');
  const MM = String(t.getUTCMonth() + 1).padStart(2, '0');
  void HH;
  return { tsLabel: `${realHH}:${mm} ${DD}/${MM}`, status, summary };
}

// Wrapper de map cron co stable key (React khong cho key tren Fragment shorthand).
function CronRowFragment({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function CronJobsSection({ filter }: { filter: string }) {
  const api = useAdminAgentsApi<CronApiResponse>('/cron-jobs');
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!api.data) return [];
    const f = filter.trim().toLowerCase();
    const sorted = [...api.data.crons].sort((a, b) => {
      const aPaused = a.active === false ? 1 : 0;
      const bPaused = b.active === false ? 1 : 0;
      if (aPaused !== bPaused) return aPaused - bPaused;
      return 0;
    });
    return f
      ? sorted.filter(
          (c) =>
            c.name.toLowerCase().includes(f) ||
            c.owner_agent.toLowerCase().includes(f) ||
            c.module.toLowerCase().includes(f),
        )
      : sorted;
  }, [api.data, filter]);

  return (
    <section className="agent-section">
      <h2 className="agent-section-title">2. Cron job ({rows.length}{api.data ? `/${api.data.crons.length}` : ''})</h2>
      {api.loading && <p className="agent-loading">Dang tai du lieu cron...</p>}
      {api.error && <p className="agent-error">Loi tai cron: {api.error}</p>}
      {api.data && (
        <div className="agent-cron-table-wrap">
          <table className="agent-cron-table">
            <thead>
              <tr>
                <th>Ten cron</th>
                <th>Lich (ICT)</th>
                <th>Module</th>
                <th>Owner</th>
                <th>Lan chay cuoi</th>
                <th>Trang thai</th>
                <th>Lan ke tiep</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const lr = formatLastRun(row);
                const next = nextCronRun(row.expr);
                const isOpen = expanded === row.name;
                return (
                  <CronRowFragment key={row.name}>
                    <tr
                      key={row.name}
                      className={`agent-cron-row ${isOpen ? 'agent-cron-row-open' : ''} ${row.active === false ? 'agent-cron-row-paused' : ''}`}
                      onClick={() => setExpanded(isOpen ? null : row.name)}
                    >
                      <td className="agent-cron-name">
                        {row.name}
                        {row.active === false && (
                          <span className="agent-workflow-state agent-workflow-state-paused" style={{ marginLeft: '0.4rem' }}>
                            Paused
                          </span>
                        )}
                      </td>
                      <td className="agent-cron-schedule">
                        <code className="agent-cron-expr">{row.expr}</code>
                        <br />
                        <span className="agent-cron-human">{row.human_ict}</span>
                      </td>
                      <td className="agent-cron-module">{row.module}</td>
                      <td className="agent-cron-owner">{row.owner_agent}</td>
                      <td className="agent-cron-last">{row.active === false ? '-' : lr.tsLabel}</td>
                      <td className="agent-cron-status">
                        {row.active === false ? (
                          <span className="agent-workflow-state agent-workflow-state-paused">Paused</span>
                        ) : (
                          <StatusPill status={lr.status} />
                        )}
                      </td>
                      <td className="agent-cron-next">
                        {row.active === false ? (
                          <span className="agent-cron-next-rel">khong chay</span>
                        ) : next ? (
                          <>
                            <span>{next.next_ict}</span>
                            <br />
                            <span className="agent-cron-next-rel">{relativeIn(next.in_minutes)}</span>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="agent-cron-detail-row" key={`${row.name}-detail`}>
                        <td colSpan={7}>
                          <div className="agent-cron-detail">
                            <p>
                              <strong>Mo ta:</strong> {row.description}
                            </p>
                            <p>
                              <strong>Nguon log:</strong>{' '}
                              <code>
                                {row.log_source.type}
                                {row.log_source.bucket ? `:${row.log_source.bucket}` : ''}
                                {row.log_source.prefix ? `/${row.log_source.prefix}` : ''}
                                {row.log_source.key ? `[${row.log_source.key}]` : ''}
                                {row.log_source.table ? `[${row.log_source.table}]` : ''}
                              </code>
                            </p>
                            <p>
                              <strong>Tom tat last_run:</strong>{' '}
                              <span className="agent-cron-summary">{lr.summary || '(chua co)'}</span>
                            </p>
                            <p className="agent-cron-note">
                              Phase 0: trang nay chi xem. Nut "Chay lai" defer Phase 2.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </CronRowFragment>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="agent-cron-empty">
                    Khong co cron nao khop tu khoa "{filter}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ActivityLogSection() {
  const api = useAdminAgentsApi<ActivityResponse>('/activity?limit=50');
  return (
    <section className="agent-section">
      <h2 className="agent-section-title">3. Nhat ky hoat dong (R2 audit, 48h gan day)</h2>
      <p className="agent-section-sub">
        50 dong moi nhat tu cac prefix R2 <code>asklaw-audit-logs</code>. Window: hom nay + hom qua (UTC).
      </p>
      {api.loading && <p className="agent-loading">Dang tai nhat ky...</p>}
      {api.error && <p className="agent-error">Loi tai nhat ky: {api.error}</p>}
      {api.data && (
        <div className="agent-activity-log">
          {api.data.lines.length === 0 ? (
            <p className="agent-empty">
              Chua co log nao trong 48h gan day. Cron co the chua chay hoac log o prefix khac.
            </p>
          ) : (
            <ol className="agent-activity-list">
              {api.data.lines.map((ln, i) => (
                <li key={`${ln.cron}-${i}-${ln.ts}`} className="agent-activity-line">
                  <span className="agent-activity-ts">{ln.ts || '(thieu ts)'}</span>
                  <span className="agent-activity-cron">[{ln.cron}]</span>
                  <span className="agent-activity-body">{ln.line.slice(0, 240)}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

function WorkflowSection({ crons }: { crons: CronJsonRow[] }) {
  // Sort: active truoc, sau do theo module + expr.
  const sorted = [...crons].sort((a, b) => {
    const aActive = a.active !== false ? 0 : 1;
    const bActive = b.active !== false ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    if (a.module !== b.module) return a.module.localeCompare(b.module);
    return a.expr.localeCompare(b.expr);
  });
  const activeCount = sorted.filter((c) => c.active !== false).length;
  const pausedCount = sorted.length - activeCount;

  return (
    <section className="agent-section">
      <h2 className="agent-section-title">
        4. Workflow chi tiet ({sorted.length} cron - {activeCount} active, {pausedCount} paused)
      </h2>
      <p className="agent-section-sub">
        Bang lieu ke toan bo cron job (ca active va paused), agent chu so huu, lich chay nguoi doc duoc, va muc dich.
        Paused cron la nhung cron comment-out trong <code>wrangler.toml</code>, giu de tham khao + re-activate nhanh.
      </p>
      <div className="agent-workflow-table-wrap">
        <table className="agent-workflow-table">
          <thead>
            <tr>
              <th>Cron expr</th>
              <th>Agent chu so huu</th>
              <th>Module / Job</th>
              <th>Trang thai</th>
              <th>Lich (nguoi doc)</th>
              <th>Muc dich</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => {
              const isActive = c.active !== false;
              return (
                <tr key={c.name} className={isActive ? 'agent-workflow-row-active' : 'agent-workflow-row-paused'}>
                  <td><code className="agent-workflow-expr">{c.expr}</code></td>
                  <td>{c.owner_agent}</td>
                  <td>
                    <span className="agent-workflow-module">{c.module}</span>
                    <br />
                    <span className="agent-workflow-name">{c.name}</span>
                  </td>
                  <td>
                    {isActive ? (
                      <span className="agent-workflow-state agent-workflow-state-active">Active</span>
                    ) : (
                      <span className="agent-workflow-state agent-workflow-state-paused">Paused</span>
                    )}
                  </td>
                  <td>{c.human_ict ?? '-'}</td>
                  <td className="agent-workflow-purpose">{c.description ?? c.wrangler_comment ?? '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AgentsInner() {
  const [filter, setFilter] = useState('');
  const agents = (agentsData as { agents: AgentMeta[] }).agents;
  const cronList = (cronJobsData as { crons: CronJsonRow[] }).crons;

  const cronCountByAgent: Record<string, number> = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of cronList) out[c.owner_agent] = (out[c.owner_agent] ?? 0) + 1;
    return out;
  }, [cronList]);

  return (
    <main className="dashboard-shell agent-dashboard">
      <header className="agent-header">
        <div>
          <h1 className="agent-title">Bang dieu khien Agents + Cron</h1>
          <p className="agent-sub">
            7 agent dinh nghia + {cronList.length} cron job. Theo doi schedule, lan chay cuoi, va trang thai. Du lieu
            agent dong bo tu <code>.claude/agents/*.md</code> qua script <code>scripts/sync-agents-metadata.mjs</code>.
          </p>
        </div>
        <div className="agent-filter-bar">
          <label htmlFor="agent-filter-input" className="agent-filter-label">
            Tim agent hoac cron
          </label>
          <input
            id="agent-filter-input"
            className="agent-filter-input"
            type="search"
            placeholder="Vi du: marketer, news, MD-26"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </header>

      <AgentRosterSection agents={agents} cronCountByAgent={cronCountByAgent} filter={filter} />
      <CronJobsSection filter={filter} />
      <ActivityLogSection />
      <WorkflowSection crons={cronList} />

      <footer className="agent-footer">
        <p>
          Phien ban Phase 0 - chi danh cho admin Asklaw. Du lieu agent dong bo lan cuoi khi chay
          script sync (xem timestamp trong file JSON). Cron rerun va Cost dashboard se ra mat o phase sau.
        </p>
      </footer>
    </main>
  );
}

export function AgentDashboardRoute() {
  return (
    <DashboardGate>
      <DashboardLayout>
        <AgentsInner />
      </DashboardLayout>
    </DashboardGate>
  );
}
